// hooks/useNotificationRules.js
import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { ENDPOINTS } from '../services/endpoints';

export const useNotificationRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all rules
  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get(ENDPOINTS.NOTIFICATIONS_ADMIN_RULES);
      setRules(response || []);
    } catch (err) {
      console.error('Error fetching notification rules:', err);
      setError(err.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  // Toggle rule enabled/disabled
    const toggleRule = useCallback(async (ruleId, currentStatus) => {
    try {
        const newStatus = !currentStatus;
        
        console.log('🔄 Toggling rule:', ruleId, 'from', currentStatus, 'to', newStatus);
        
        // ✅ FIX: Construct URL with query parameter
        const url = `${ENDPOINTS.NOTIFICATIONS_ADMIN_TOGGLE(ruleId)}?enabled=${newStatus}`;
        console.log('📡 Request URL:', url);
        
        const response = await apiService.post(url);
        
        console.log('✅ Toggle response:', response);
        
        // Update local state immediately
        setRules(prev =>
        prev.map(rule =>
            rule.rule_id === ruleId ? { ...rule, enabled: newStatus } : rule
        )
        );
        
        return true;
    } catch (err) {
        console.error('❌ Error toggling rule:', err);
        console.error('   Error response:', err.response?.data);
        
        // Revert on error
        await fetchRules();
        
        throw err;
    }
    }, [fetchRules]);

  // Update rule schedule
  const updateSchedule = useCallback(async (ruleId, scheduleType, scheduleConfig) => {
    try {
      const response = await apiService.post(ENDPOINTS.NOTIFICATIONS_ADMIN_SCHEDULE(ruleId), {
        schedule_type: scheduleType,
        schedule_config: scheduleConfig,
      });
      
      // Refresh rules to get updated next_run_time
      await fetchRules();
      
      return response;
    } catch (err) {
      console.error('Error updating schedule:', err);
      throw err;
    }
  }, [fetchRules]);

  // Update rule roles
  const updateRoles = useCallback(async (ruleId, allowedRoles) => {
    try {
      await apiService.post(ENDPOINTS.NOTIFICATIONS_ADMIN_ROLES(ruleId), {
        allowed_roles: allowedRoles,
      });
      
      // Update local state
      setRules(prev =>
        prev.map(rule =>
          rule.rule_id === ruleId ? { ...rule, allowed_roles: allowedRoles } : rule
        )
      );
      
      return true;
    } catch (err) {
      console.error('Error updating roles:', err);
      throw err;
    }
  }, []);

  // Manually trigger rule
  const runRuleNow = useCallback(async (ruleId) => {
    try {
      const response = await apiService.post(ENDPOINTS.NOTIFICATIONS_ADMIN_RUN_NOW(ruleId));
      
      // Refresh rules to get updated last_run_time
      await fetchRules();
      
      return response;
    } catch (err) {
      console.error('Error running rule:', err);
      throw err;
    }
  }, [fetchRules]);

  // Get run history
  const getRunHistory = useCallback(async (ruleId, limit = 20) => {
    try {
      const response = await apiService.get(ENDPOINTS.NOTIFICATIONS_ADMIN_HISTORY(ruleId), {
        limit,
      });
      return response;
    } catch (err) {
      console.error('Error fetching run history:', err);
      throw err;
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  return {
    rules,
    loading,
    error,
    fetchRules,
    toggleRule,
    updateSchedule,
    updateRoles,
    runRuleNow,
    getRunHistory,
  };
};